import React, { useMemo, useState } from 'react';
import { GenericForm, type GenericFormField } from '@/styles/GenericForm';
import { costCodeLabel, costCodeProblem, normalizeCostCode } from '@/lib/cost-codes';
import { saveCostCode, type CostCode } from '../services/catalog-api';

interface CostCodeFormProps {
  /** null = a new cost code */
  costCode: CostCode | null;
  /** every cost code of the company (to offer the valid parents and to know the children) */
  codes: CostCode[];
  isOpen: boolean;
  onClose: () => void;
  onSaved: () => void;
}

/**
 * Create / edit a cost code: code and name side by side, the Parent Cost Code and Active under them, the description across the full width.
 * ONE entity, two levels: no parent selected = this cost code is a Parent (it groups child cost codes and is never used on items, orders or bills);
 * a parent selected = it is a Child of it. Only Parents can be chosen (never a child, never itself); a Parent that has children cannot be given
 * a parent until its children are moved; the default cost code stays under 0000 · Unassigned. Records reference the code by id, not by its text.
 * Deleting is done from the table (checkboxes + the menu), never from here.
 */
export function CostCodeForm({ costCode, codes, isOpen, onClose, onSaved }: CostCodeFormProps) {
  const [error, setError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const [parentId, setParentId] = useState<string | null>(null);
  const isDefault = !!costCode?.default_key;
  const childCount = costCode ? codes.filter((c) => c.parent_id === costCode.id).length : 0;

  const parents = useMemo(() => codes.filter((c) => !c.parent_id && c.id !== costCode?.id), [codes, costCode]);
  const selectedParent = codes.find((c) => c.id === (parentId ?? costCode?.parent_id ?? ''));

  const explanation = (() => {
    if (childCount > 0) return `This Cost Code is a parent with ${childCount} child cost code${childCount > 1 ? 's' : ''}. Move or delete them before giving it a parent.`;
    if (isDefault) return 'The default Cost Code always stays under its parent.';
    return selectedParent
      ? `This Cost Code will be a child of ${costCodeLabel(selectedParent)}.`
      : 'This Cost Code will be a parent category that groups child Cost Codes.';
  })();

  const fields = useMemo<GenericFormField[]>(() => [
    { name: 'code', label: 'Code', type: 'text', required: true, row: 1, placeholder: 'e.g. 1100' },
    { name: 'name', label: 'Name', type: 'text', row: 1, placeholder: 'Name' },
    {
      name: 'parent_id',
      label: 'Parent Cost Code',
      type: 'select',
      row: 2,
      placeholder: 'None (this is a parent)',
      disabled: isDefault || childCount > 0,
      options: parents.map((c) => ({ label: costCodeLabel(c), value: c.id })),
      helperText: explanation,
    },
    // the default code is the fallback of items without one: it stays active
    { name: 'is_active', label: 'Active', type: 'checkbox', row: 2, disabled: isDefault },
    { name: 'description', label: 'Description', type: 'textarea', row: 3, colSpan: 2, rows: 3, placeholder: 'Description' },
  ], [isDefault, childCount, parents, explanation]);

  const initialValues = useMemo(() => ({
    code: costCode?.code ?? '',
    name: costCode?.name ?? '',
    parent_id: costCode?.parent_id ?? '',
    description: costCode?.description ?? '',
    is_active: costCode ? costCode.is_active : true,
  }), [costCode]);

  const handleSubmit = async (data: Record<string, any>) => {
    if (saving) return;
    const code = normalizeCostCode(data.code);
    const problem = costCodeProblem(code);
    if (problem) { setError(problem); return; }
    setSaving(true);
    setError(null);
    try {
      await saveCostCode(costCode?.id ?? null, { code, name: data.name, description: data.description, is_active: data.is_active === true, parent_id: data.parent_id || null });
      onSaved();
      onClose();
    } catch (failure) {
      setError(failure instanceof Error ? failure.message : 'Failed to save the cost code');
    } finally {
      setSaving(false);
    }
  };

  return (
    <GenericForm
      title="Cost Code"
      mode={costCode ? 'edit' : 'create'}
      fields={fields}
      initialValues={initialValues}
      onSubmit={handleSubmit}
      onFieldChange={(name, value) => { if (name === 'parent_id') setParentId(value || ''); }}
      onCancel={() => { setError(null); setParentId(null); onClose(); }}
      isOpen={isOpen}
      error={error}
      saving={saving}
      size="compact"
      compactWidthClassName="max-w-2xl"
    />
  );
}
